import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface NotificationImageUploadProps {
  imageUrl: string;
  onImageChange: (url: string) => void;
}

export default function NotificationImageUpload({
  imageUrl,
  onImageChange,
}: NotificationImageUploadProps) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(t("admin.notifications.invalidFileType", "Tipo file non valido"));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("admin.notifications.fileTooLarge", "File troppo grande (max 5MB)"));
      return;
    }

    setIsUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `notifications/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("notification-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("notification-images")
        .getPublicUrl(filePath);

      onImageChange(urlData.publicUrl);
      toast.success(t("admin.notifications.imageUploaded", "Immagine caricata"));
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(t("common.error"));
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    onImageChange("");
  };

  return (
    <div>
      {imageUrl ? (
        <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-dark">
          <img
            src={imageUrl}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-destructive text-white rounded-full hover:bg-destructive/80 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="aspect-video rounded-lg border-2 border-dashed border-dark/40 bg-surface hover:bg-surface/80 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2"
        >
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t("admin.notifications.uploadImage", "Clicca per caricare un'immagine")}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("admin.notifications.imageRatio", "Formato consigliato: 16:9")}
              </span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
